import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, Send, Bot, User, Loader2, X, Minimize2, Maximize2,
  Sparkles, AlertTriangle, FileText, Scale, DollarSign, HelpCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import api from '@/lib/api';

const suggestedQuestions = [
  { icon: AlertTriangle, text: "What are the top 3 risks I should focus on?", category: "risks" },
  { icon: Scale, text: "Are there any concerning legal clauses?", category: "legal" },
  { icon: DollarSign, text: "What's our total financial exposure?", category: "financial" },
  { icon: FileText, text: "Summarize the scope of work", category: "scope" },
  { icon: HelpCircle, text: "What questions should I ask the client?", category: "clarification" },
];

export function TenderAIChat({ tenderId, tenderName, isAnalyzed }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Auto scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Focus input when opened
  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);
  
  // Load chat history
  useEffect(() => {
    if (isOpen && tenderId) {
      loadHistory();
    }
  }, [isOpen, tenderId]);
  
  const loadHistory = async () => {
    try {
      const response = await api.get(`/tenders/${tenderId}/chat/history`);
      const history = response.data.map(h => ([
        { role: 'user', content: h.user_message },
        { role: 'assistant', content: h.assistant_response }
      ])).flat();
      setMessages(history);
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };
  
  const sendMessage = async (text = input) => {
    if (!text.trim() || loading) return;
    
    const userMessage = { role: 'user', content: text.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    
    try {
      const response = await api.post(`/tenders/${tenderId}/chat`, {
        message: text.trim(),
        history: messages.slice(-6) // Send last 6 messages for context
      });
      
      const assistantMessage = { role: 'assistant', content: response.data.response };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.',
        isError: true
      }]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  // Format message content with markdown-like styling
  const formatMessage = (content) => {
    if (!content) return '';
    
    // Split into paragraphs and format
    return content.split('\n').map((line, i) => {
      // Headers
      if (line.startsWith('## ')) {
        return <h3 key={i} className="text-sm font-semibold text-white mt-3 mb-1">{line.slice(3)}</h3>;
      }
      if (line.startsWith('# ')) {
        return <h2 key={i} className="text-base font-bold text-white mt-3 mb-1">{line.slice(2)}</h2>;
      }
      // Bullet points
      if (line.startsWith('- ') || line.startsWith('• ')) {
        return <li key={i} className="text-sm text-slate-300 ml-4">{line.slice(2)}</li>;
      }
      // Numbered lists
      if (/^\d+\.\s/.test(line)) {
        return <li key={i} className="text-sm text-slate-300 ml-4 list-decimal">{line.slice(line.indexOf('.') + 2)}</li>;
      }
      // Bold text
      if (line.includes('**')) {
        const parts = line.split(/\*\*(.*?)\*\*/g);
        return (
          <p key={i} className="text-sm text-slate-300 mb-1">
            {parts.map((part, j) => j % 2 === 1 ? <strong key={j} className="text-white">{part}</strong> : part)}
          </p>
        );
      }
      // Empty lines
      if (!line.trim()) return <br key={i} />;
      // Regular text
      return <p key={i} className="text-sm text-slate-300 mb-1">{line}</p>;
    });
  };
  
  if (!isOpen) {
    return (
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 right-6 z-[9999] w-14 h-14 rounded-full bg-gradient-to-r from-[#1E4E8C] to-[#143A6B] shadow-lg shadow-[#1E4E8C]/30 flex items-center justify-center group"
        data-testid="ai-chat-fab"
      >
        <Bot className="w-6 h-6 text-white" />
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full animate-pulse" />
        <motion.span 
          initial={{ opacity: 0, x: 10 }}
          whileHover={{ opacity: 1, x: 0 }}
          className="absolute right-16 whitespace-nowrap bg-[#1F2937] px-3 py-1.5 rounded-lg text-sm text-white shadow-lg"
        >
          Ask TenderAI
        </motion.span>
      </motion.button>
    );
  }
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.95 }}
        className={`fixed z-[9999] ${
          isMinimized 
            ? 'bottom-24 right-6 w-72' 
            : 'bottom-24 right-6 w-[420px] h-[550px]'
        } bg-[#0A0E1A] border border-[#3A3F4A] rounded-2xl shadow-2xl shadow-black/50 overflow-hidden flex flex-col`}
        data-testid="ai-chat-window"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#3A3F4A] bg-gradient-to-r from-[#1F2937] to-[#0A0E1A]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1E4E8C] to-[#143A6B] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">TenderAI Assistant</h3>
              <p className="text-xs text-slate-500 truncate max-w-[200px]">{tenderName}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors"
            >
              {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {!isMinimized && (
          <>
            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              {messages.length === 0 ? (
                <div className="space-y-4">
                  <div className="text-center py-6">
                    <Bot className="w-12 h-12 mx-auto text-[#1E4E8C] mb-3" />
                    <h4 className="text-white font-medium mb-1">Hi! I'm TenderAI</h4>
                    <p className="text-sm text-slate-400">
                      {isAnalyzed 
                        ? "I can help you understand this tender's risks, requirements, and recommendations."
                        : "Upload and analyze a document first, then I can help you understand it."}
                    </p>
                  </div>
                  
                  {isAnalyzed && (
                    <div className="space-y-2">
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Suggested Questions</p>
                      {suggestedQuestions.map((q, i) => (
                        <button
                          key={i}
                          onClick={() => sendMessage(q.text)}
                          className="w-full flex items-center gap-3 p-3 rounded-lg bg-[#1F2937] border border-[#3A3F4A] hover:border-[#1E4E8C]/50 hover:bg-[#1E4E8C]/5 transition-all text-left group"
                        >
                          <q.icon className="w-4 h-4 text-slate-500 group-hover:text-[#1E4E8C] transition-colors" />
                          <span className="text-sm text-slate-300 group-hover:text-white transition-colors">{q.text}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center ${
                        msg.role === 'user' 
                          ? 'bg-[#1E4E8C]/20 text-[#1E4E8C]' 
                          : 'bg-purple-500/20 text-purple-400'
                      }`}>
                        {msg.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                      </div>
                      <div className={`flex-1 ${msg.role === 'user' ? 'text-right' : ''}`}>
                        <div className={`inline-block max-w-[85%] rounded-2xl p-3 ${
                          msg.role === 'user'
                            ? 'bg-[#1E4E8C] text-white rounded-tr-sm'
                            : msg.isError
                              ? 'bg-red-500/10 border border-red-500/20 rounded-tl-sm'
                              : 'bg-[#1F2937] border border-[#3A3F4A] rounded-tl-sm'
                        }`}>
                          {msg.role === 'user' ? (
                            <p className="text-sm">{msg.content}</p>
                          ) : (
                            <div className="text-left">
                              {formatMessage(msg.content)}
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                  
                  {loading && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex gap-3"
                    >
                      <div className="w-8 h-8 rounded-lg bg-purple-500/20 text-purple-400 flex items-center justify-center">
                        <Bot className="w-4 h-4" />
                      </div>
                      <div className="bg-[#1F2937] border border-[#3A3F4A] rounded-2xl rounded-tl-sm p-3">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-[#1E4E8C]" />
                          <span className="text-sm text-slate-400">Thinking...</span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>
            
            {/* Input */}
            <div className="p-4 border-t border-[#3A3F4A] bg-[#1F2937]">
              <div className="flex items-center gap-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={isAnalyzed ? "Ask about risks, clauses, compliance..." : "Analyze a document first..."}
                  disabled={loading || !isAnalyzed}
                  className="flex-1 bg-[#0A0E1A] border-[#3A3F4A] text-white placeholder:text-slate-500 focus:border-[#1E4E8C]"
                  data-testid="ai-chat-input"
                />
                <Button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || loading || !isAnalyzed}
                  className="bg-[#1E4E8C] hover:bg-[#4a72ff] text-white px-4"
                  data-testid="ai-chat-send"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

export default TenderAIChat;
